import { Either, pipe } from 'effect';

import {
  INVISIBLE_CHARS_RE,
  isCompatibleOverride,
  isPlainSignature,
  messageSignature,
} from '@/lib/i18nOverrides/messageSignature';

export type Messages = Readonly<Record<string, string>>;

export interface DroppedOverride {
  readonly key: string;
  readonly reason: 'signature' | 'unparseable' | 'invisible';
}

export interface MergedMessages {
  /** Static catalog with every compatible override applied. */
  readonly messages: Messages;
  /**
   * Overrides of keys the catalog lacks. Their base is the code's default text, known only at format time, so the
   * intl wrapper checks them there (see `withDeferredOverrides`).
   */
  readonly deferred: Messages;
  readonly dropped: ReadonlyArray<DroppedOverride>;
}

const dropReason = (value: string): DroppedOverride['reason'] => {
  if (INVISIBLE_CHARS_RE.test(value)) return 'invisible';
  return pipe(
    messageSignature(value),
    Either.match({ onLeft: () => 'unparseable' as const, onRight: () => 'signature' as const }),
  );
};

/**
 * static ⊕ org overrides, with the runtime guard: react-intl does NOT fall back to defaultMessage when a message
 * fails to format — it renders the raw source ("Hello {name}") — so an override with a different placeholder/tag set,
 * that does not parse, or that carries marker chars must never reach it.
 */
export const mergeOverrides = (base: Messages, overrides: Messages): MergedMessages => {
  type Verdict =
    | { readonly kind: 'applied' | 'deferred'; readonly key: string; readonly value: string }
    | { readonly kind: 'dropped'; readonly dropped: DroppedOverride };

  const verdicts = Object.entries(overrides).map(([key, value]): Verdict => {
    const baseMessage = base[key];
    if (baseMessage === undefined) {
      const parses = !INVISIBLE_CHARS_RE.test(value) && Either.isRight(messageSignature(value));
      return parses ? { kind: 'deferred', key, value } : { kind: 'dropped', dropped: { key, reason: dropReason(value) } };
    }
    return isCompatibleOverride(baseMessage, value)
      ? { kind: 'applied', key, value }
      : { kind: 'dropped', dropped: { key, reason: dropReason(value) } };
  });

  const entriesOf = (kind: 'applied' | 'deferred') =>
    Object.fromEntries(verdicts.flatMap((v) => (v.kind === kind ? [[v.key, v.value] as const] : [])));

  return {
    // One spread of the catalog, not one per override: the catalog has thousands of keys.
    messages: { ...base, ...entriesOf('applied') },
    deferred: entriesOf('deferred'),
    dropped: verdicts.flatMap((v) => (v.kind === 'dropped' ? [v.dropped] : [])),
  };
};

/** Whether a deferred override may replace the code's default text (or, with none, whether it is plain text). */
export const isDeferredOverrideApplicable = (defaultMessage: string | undefined, value: string): boolean =>
  defaultMessage === undefined
    ? pipe(messageSignature(value), Either.match({ onLeft: () => false, onRight: isPlainSignature }))
    : isCompatibleOverride(defaultMessage, value);
